// src/pages/B2B/crm/VenueDetailPage.tsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { getAdminApiUrl } from '@/pages/Admin/shared';
import { useVenue, useCrmMutations, useVenueList } from './api/crmClient';
import {
  Venue, VenueContact, PIPELINE_STAGES, OUTREACH_STATUSES,
  VENUE_TYPES, OWNERSHIP_TYPES, SUBSCRIPTION_TIERS, PipelineStage, Meeting,
} from './api/types';
import { useMeetingList } from './api/meetingsClient';
import { MeetingModal } from './components/MeetingModal';
import { PipelineStageBadge } from './components/PipelineStageBadge';
import { SelectWithOther } from './components/SelectWithOther';
import { VenueContactList } from './components/VenueContactList';

const EMPTY_VENUE: Partial<Venue> = {
  name: '',
  pipeline_stage: '1-Target' as PipelineStage,
  flag_for_ray: false,
};

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <div className="text-gray-500 text-[10px] uppercase tracking-wide mt-2 mb-1">{children}</div>;
}

function TextInput({ value, onChange, ...rest }: { value: string | null | undefined; onChange: (v: string) => void } & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'>) {
  return (
    <input
      {...rest}
      className="w-full bg-[#0f0f1a] border border-[#2a2a3e] rounded px-2 py-1.5 text-sm text-gray-200"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

function VenueDetailPage(): React.ReactElement {
  const apiUrl = getAdminApiUrl('prod');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isCreate = !id || id === 'new';

  const { data, loading, error: loadError, refetch } = useVenue(apiUrl, isCreate ? undefined : id);
  const { createVenue, updateVenue, deleteVenue, createContact, updateContact, deleteContact } = useCrmMutations(apiUrl);

  const [draft, setDraft] = useState<Partial<Venue>>(EMPTY_VENUE);
  const [contacts, setContacts] = useState<VenueContact[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const nowIso = useMemo(() => new Date().toISOString(), []);
  const meetingsFilter = useMemo(
    () => (id ? { venue_id: id, from: nowIso } : { venue_id: undefined, from: nowIso }),
    [id, nowIso],
  );
  const { data: upcoming, refetch: refetchMeetings } =
    useMeetingList(apiUrl, isCreate ? {} : meetingsFilter);
  const { data: venuesForModal } = useVenueList(apiUrl, {});
  const [meetingModalOpen, setMeetingModalOpen] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState<Meeting | null>(null);

  const draftSeededFor = useRef<string | null>(null);
  useEffect(() => {
    if (data) {
      if (draftSeededFor.current !== data.venue.id) {
        setDraft(data.venue);
        draftSeededFor.current = data.venue.id;
      }
      setContacts(data.contacts);
    }
  }, [data]);

  const setField = <K extends keyof Venue>(k: K, v: Venue[K]) => setDraft(d => ({ ...d, [k]: v }));

  const onSave = async () => {
    setSaving(true); setError(null);
    try {
      if (isCreate) {
        const contactsPayload = contacts.map(({ name, title, phone, email, kind }) => ({
          name, title, phone, email, kind,
        }));
        await createVenue({
          ...(draft as any),
          name: draft.name ?? '',
          ...(contactsPayload.length > 0 ? { contacts: contactsPayload } : {}),
        });
      } else {
        await updateVenue(id!, draft);
      }
      navigate('/b2b/crm', { replace: true });
    } catch (e: any) {
      setError(e?.message ?? 'Save failed');
    } finally { setSaving(false); }
  };

  // Local-only contact mutations for create mode. Backend persists the array
  // atomically inside the createVenue transaction, so we just buffer rows here.
  const onCreateLocalContact = async (c: { name: string; kind: VenueContact['kind'] } & Partial<VenueContact>) => {
    setContacts(prev => [...prev, {
      id: (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `tmp-${Date.now()}-${prev.length}`,
      venue_id: '',
      name: c.name,
      title: c.title ?? null,
      phone: c.phone ?? null,
      email: c.email ?? null,
      kind: c.kind,
      sort_order: prev.length,
      created_at: '',
      updated_at: '',
    }]);
  };
  const onUpdateLocalContact = async (cid: string, patch: Partial<VenueContact>) => {
    setContacts(prev => prev.map(c => c.id === cid ? { ...c, ...patch } : c));
  };
  const onDeleteLocalContact = async (cid: string) => {
    setContacts(prev => prev.filter(c => c.id !== cid));
  };

  const onDelete = async () => {
    if (!id || isCreate) return;
    if (!window.confirm(`Soft-delete "${draft.name}"? It will be hidden from the list but recoverable from the database.`)) return;
    setSaving(true); setError(null);
    try {
      await deleteVenue(id);
      navigate('/b2b/crm');
    } catch (e: any) {
      setError(e?.message ?? 'Delete failed');
      setSaving(false);
    }
  };

  if (!isCreate && loading) {
    return <div className="min-h-screen bg-[#0f0f1a] py-12 text-center text-gray-500">Loading...</div>;
  }
  if (!isCreate && loadError) {
    return <div className="min-h-screen bg-[#0f0f1a] py-12 text-center text-red-400">{loadError}</div>;
  }

  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      <div className="max-w-6xl mx-auto px-6 pt-8 pb-16">
        <Link to="/b2b/crm" className="text-violet-400 text-sm no-underline">← All venues</Link>

        <div className="flex justify-between items-start mt-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-white">
              {isCreate ? 'New Venue' : draft.name || '(unnamed)'}
            </h1>
            {!isCreate && (
              <div className="mt-1 text-gray-500 text-xs">
                {draft.address ?? '—'} ·{' '}
                <PipelineStageBadge stage={draft.pipeline_stage ?? null} />
              </div>
            )}
            {!isCreate && draft.updated_at && (
              <div className="mt-1 text-gray-600 text-xs">
                Last updated: {new Date(draft.updated_at).toLocaleString()}
                {draft.updated_by && ` by ${draft.updated_by}`}
              </div>
            )}
          </div>
          <div className="flex gap-2">
            {!isCreate && (
              <button onClick={onDelete} disabled={saving}
                      className="text-red-400 hover:text-red-300 text-sm disabled:opacity-50 px-3 py-2">
                Delete
              </button>
            )}
            <button onClick={onSave} disabled={saving || !draft.name}
                    className="bg-violet-600 hover:bg-violet-500 text-white text-sm font-semibold rounded-md px-4 py-2 disabled:opacity-50">
              {saving ? 'Saving...' : (isCreate ? 'Create' : 'Save changes')}
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-700/40 text-red-300 rounded-md p-3 text-sm mb-4">{error}</div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Pipeline */}
          <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-lg p-4">
            <div className="text-violet-400 text-xs font-semibold mb-2">PIPELINE</div>
            <FieldLabel>Stage</FieldLabel>
            <select className="w-full bg-[#0f0f1a] border border-[#2a2a3e] rounded px-2 py-1.5 text-sm text-gray-200"
                    value={draft.pipeline_stage ?? ''}
                    onChange={(e) => setField('pipeline_stage', (e.target.value || null) as PipelineStage | null)}>
              <option value="">—</option>
              {PIPELINE_STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <FieldLabel>Corporate Outreach</FieldLabel>
            <SelectWithOther
              value={draft.corporate_outreach_status ?? null}
              onChange={(v) => setField('corporate_outreach_status', v)}
              options={OUTREACH_STATUSES}
            />
            <FieldLabel>Next Action</FieldLabel>
            <TextInput value={draft.next_action} onChange={(v) => setField('next_action', v || null)} />
            <FieldLabel>Next Action Date</FieldLabel>
            <TextInput type="date" value={draft.next_action_date} onChange={(v) => setField('next_action_date', v || null)} />
            <FieldLabel>Last Contact Date</FieldLabel>
            <TextInput type="date" value={draft.last_contact_date} onChange={(v) => setField('last_contact_date', v || null)} />
            <label className="flex items-center gap-2 mt-3 text-sm text-gray-300">
              <input type="checkbox" checked={!!draft.flag_for_ray}
                     onChange={(e) => setField('flag_for_ray', e.target.checked)} />
              Flag for Ray
            </label>
          </div>

          {/* Contacts */}
          <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-lg p-4">
            <div className="text-violet-400 text-xs font-semibold mb-2">CONTACTS</div>
            {isCreate ? (
              <VenueContactList
                contacts={contacts}
                onCreate={onCreateLocalContact}
                onUpdate={onUpdateLocalContact}
                onDelete={onDeleteLocalContact}
              />
            ) : (
              <VenueContactList
                contacts={contacts}
                onCreate={async (c) => { await createContact(id!, c); refetch(); }}
                onUpdate={async (cid, patch) => { await updateContact(id!, cid, patch); refetch(); }}
                onDelete={async (cid) => { await deleteContact(id!, cid); refetch(); }}
              />
            )}
          </div>

          {/* Engagement */}
          <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-lg p-4">
            <div className="text-violet-400 text-xs font-semibold mb-2">ENGAGEMENT</div>
            <FieldLabel>Last 7d Check-Ins</FieldLabel>
            <TextInput type="number" value={String(draft.last_7d_checkins ?? '')}
                       onChange={(v) => setField('last_7d_checkins', v ? Number(v) : null)} />
            <FieldLabel>Last 30d Check-Ins</FieldLabel>
            <TextInput type="number" value={String(draft.last_30d_checkins ?? '')}
                       onChange={(v) => setField('last_30d_checkins', v ? Number(v) : null)} />
            <FieldLabel>Peak Night</FieldLabel>
            <TextInput value={draft.peak_night} onChange={(v) => setField('peak_night', v || null)} />
            <FieldLabel>Pushes Sent</FieldLabel>
            <TextInput type="number" value={String(draft.pushes_sent ?? 0)}
                       onChange={(v) => setField('pushes_sent', v ? Number(v) : 0)} />
            <label className="flex items-center gap-2 mt-3 text-sm text-gray-300">
              <input type="checkbox" checked={!!draft.social_media_mention}
                     onChange={(e) => setField('social_media_mention', e.target.checked)} />
              Social media mention this week
            </label>
            <label className="flex items-center gap-2 mt-1 text-sm text-gray-300">
              <input type="checkbox" checked={!!draft.docsend_opened}
                     onChange={(e) => setField('docsend_opened', e.target.checked)} />
              DocSend opened
            </label>
            <FieldLabel>DocSend Open Date</FieldLabel>
            <TextInput type="date" value={draft.docsend_open_date} onChange={(v) => setField('docsend_open_date', v || null)} />
          </div>

          {/* Notes */}
          <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-lg p-4">
            <div className="text-violet-400 text-xs font-semibold mb-2">NOTES</div>
            <FieldLabel>Last Contact Notes</FieldLabel>
            <textarea rows={4}
                      className="w-full bg-[#0f0f1a] border border-[#2a2a3e] rounded px-2 py-1.5 text-sm text-gray-200"
                      value={draft.last_contact_notes ?? ''}
                      onChange={(e) => setField('last_contact_notes', e.target.value || null)} />
            <FieldLabel>General Notes</FieldLabel>
            <textarea rows={4}
                      className="w-full bg-[#0f0f1a] border border-[#2a2a3e] rounded px-2 py-1.5 text-sm text-gray-200"
                      value={draft.notes ?? ''}
                      onChange={(e) => setField('notes', e.target.value || null)} />
          </div>

          {/* Venue Info */}
          <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-lg p-4">
            <div className="text-violet-400 text-xs font-semibold mb-2">VENUE INFO</div>
            <FieldLabel>Name</FieldLabel>
            <TextInput value={draft.name} onChange={(v) => setField('name', v)} />
            <FieldLabel>Address</FieldLabel>
            <TextInput value={draft.address} onChange={(v) => setField('address', v || null)} />
            <FieldLabel>Venue Type</FieldLabel>
            <SelectWithOther
              value={draft.venue_type ?? null}
              onChange={(v) => setField('venue_type', v)}
              options={VENUE_TYPES}
            />
            <FieldLabel>Ownership Type</FieldLabel>
            <SelectWithOther
              value={draft.ownership_type ?? null}
              onChange={(v) => setField('ownership_type', v)}
              options={OWNERSHIP_TYPES}
            />
            <FieldLabel>Parent Company</FieldLabel>
            <TextInput value={draft.parent_company} onChange={(v) => setField('parent_company', v || null)} />
            <FieldLabel>Total Locations</FieldLabel>
            <TextInput type="number" value={String(draft.total_locations ?? '')}
                       onChange={(v) => setField('total_locations', v ? Number(v) : null)} />
          </div>

          {/* Commercial */}
          <div className="bg-[#1a1a2e] border border-[#2a2a3e] rounded-lg p-4">
            <div className="text-violet-400 text-xs font-semibold mb-2">COMMERCIAL</div>
            <FieldLabel>Subscription Tier</FieldLabel>
            <SelectWithOther
              value={draft.subscription_tier ?? null}
              onChange={(v) => setField('subscription_tier', v)}
              options={SUBSCRIPTION_TIERS}
            />
            <FieldLabel>Monthly Revenue</FieldLabel>
            <TextInput value={draft.monthly_revenue} onChange={(v) => setField('monthly_revenue', v || null)} />
            <FieldLabel>Warm Intro Source</FieldLabel>
            <TextInput value={draft.warm_intro_source} onChange={(v) => setField('warm_intro_source', v || null)} />
            <label className="flex items-center gap-2 mt-3 text-sm text-gray-300">
              <input type="checkbox" checked={!!draft.on_crawl_route}
                     onChange={(e) => setField('on_crawl_route', e.target.checked)} />
              On crawl route
            </label>
          </div>
        </div>

        {!isCreate && (
          <div className="mt-6 bg-[#1a1a2e] border border-[#2a2a3e] rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <div className="text-violet-400 text-xs font-semibold">UPCOMING MEETINGS</div>
              <button
                type="button"
                onClick={() => { setEditingMeeting(null); setMeetingModalOpen(true); }}
                className="text-violet-400 hover:text-violet-300 text-xs font-semibold"
              >
                + New Meeting
              </button>
            </div>
            {upcoming.meetings.length === 0 ? (
              <div className="text-gray-500 text-sm py-2">No upcoming meetings.</div>
            ) : (
              <div className="space-y-1">
                {upcoming.meetings.slice(0, 5).map(m => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => { setEditingMeeting(m); setMeetingModalOpen(true); }}
                    className="w-full text-left bg-[#0f0f1a] hover:bg-[#14141f] border border-[#2a2a3e] rounded-md px-3 py-2 flex items-center gap-3"
                  >
                    <div className="w-40 text-gray-300 text-sm">
                      {new Date(m.starts_at).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                      {!m.all_day && ` · ${new Date(m.starts_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}
                    </div>
                    <div className="flex-1 text-white text-sm truncate">{m.title}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <MeetingModal
          apiUrl={apiUrl}
          venues={venuesForModal.venues}
          open={meetingModalOpen}
          onClose={() => { setMeetingModalOpen(false); setEditingMeeting(null); }}
          onSaved={refetchMeetings}
          meeting={editingMeeting}
          defaultVenueId={id}
        />
      </div>
    </div>
  );
}

export default VenueDetailPage;
